import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Link } from 'react-router-dom';
import { Sparkles, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import MediaEmbed from '@/components/MediaEmbed';
import { useFeaturedMediaLinks, type FeaturedMediaLink } from '@/hooks/useFeaturedMediaLinks';

const ADVANCE_DELAY_MS = 8000;

interface BeAmazedSectionProps {
  className?: string;
}

const SlidePlaceholder: React.FC<{ link: FeaturedMediaLink }> = ({ link }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-white/80">
    <Play className="h-10 w-10 mb-2 opacity-70" />
    <span className="text-xs uppercase tracking-wide opacity-70">{link.category}</span>
    <span className="text-sm font-medium px-4 text-center line-clamp-2 mt-1">
      {link.context || link.solutionName}
    </span>
  </div>
);

const BeAmazedSection: React.FC<BeAmazedSectionProps> = ({ className }) => {
  const { mediaLinks, isLoading } = useFeaturedMediaLinks({ count: 3 });

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    containScroll: false,
    skipSnaps: false,
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const advanceTimerRef = useRef<number | null>(null);

  const clearAdvanceTimer = useCallback(() => {
    if (advanceTimerRef.current !== null) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, []);

  const scheduleAdvance = useCallback(() => {
    clearAdvanceTimer();
    advanceTimerRef.current = window.setTimeout(() => {
      emblaApi?.scrollNext();
    }, ADVANCE_DELAY_MS);
  }, [emblaApi, clearAdvanceTimer]);

  useEffect(() => {
    if (!emblaApi) return;

    const handleSelect = () => {
      setActiveIndex(emblaApi.selectedScrollSnap());
      clearAdvanceTimer();
    };

    handleSelect();
    emblaApi.on('select', handleSelect);
    emblaApi.on('pointerDown', clearAdvanceTimer);

    return () => {
      emblaApi.off('select', handleSelect);
      emblaApi.off('pointerDown', clearAdvanceTimer);
    };
  }, [emblaApi, clearAdvanceTimer]);

  useEffect(() => clearAdvanceTimer, [clearAdvanceTimer]);

  if (isLoading || mediaLinks.length === 0) return null;

  return (
    <section className={cn('py-12 md:py-16', className)}>
      <div className="container mx-auto px-0 md:px-4">
        <div className="text-center mb-8 px-4 md:px-0">
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              aria-label="Previous video"
              onClick={() => emblaApi?.scrollPrev()}
              className="rounded-full p-2 text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <Sparkles className="h-6 w-6 text-primary" />
            <h2 className="text-3xl font-bold">Be Amazed</h2>
            <button
              type="button"
              aria-label="Next video"
              onClick={() => emblaApi?.scrollNext()}
              className="rounded-full p-2 text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex -ml-4">
            {mediaLinks.map((link, index) => {
              const isActive = index === activeIndex;
              return (
                <div
                  key={`${link.solutionId}-${index}`}
                  className="min-w-0 shrink-0 grow-0 basis-full sm:basis-[70%] md:basis-[60%] lg:basis-[55%] pl-4"
                >
                  <Link
                    to={`/solution/${link.solutionSlug}`}
                    className={cn(
                      'group rounded-none overflow-hidden flex flex-col h-full bg-card border border-border/40 transition-all duration-300',
                      isActive
                        ? 'shadow-lg scale-100 opacity-100'
                        : 'opacity-60 scale-95'
                    )}
                  >
                    <div
                      className="relative aspect-video w-full bg-black"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      {isActive ? (
                        <MediaEmbed
                          url={link.url}
                          onEnded={scheduleAdvance}
                          className="!mt-0 absolute inset-0"
                        />
                      ) : (
                        <SlidePlaceholder link={link} />
                      )}
                    </div>

                    <div className="flex flex-col flex-grow p-5">
                      {link.context && (
                        <h3 className="mb-3 text-xl md:text-2xl font-bold text-muted-foreground text-center line-clamp-2 leading-tight">
                          {link.context}
                        </h3>
                      )}
                      <div className="mb-2 flex items-center justify-center">
                        <span className="text-xs font-medium text-muted-foreground px-2 py-1 bg-secondary rounded-full">
                          {link.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-medium text-center group-hover:text-primary transition-colors">
                        {link.solutionName}
                      </h3>
                      {link.shortDescription && (
                        <p className="mt-2 text-sm text-muted-foreground text-center line-clamp-3">
                          {link.shortDescription}
                        </p>
                      )}
                    </div>

                    <div className="p-4 flex items-center justify-center text-xs font-medium border-t border-border/40 bg-secondary/30 transition-colors group-hover:bg-secondary/70">
                      View solution
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BeAmazedSection;
